import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Collapse,
  Grid,
  IconButton,
  Link,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Language as LanguageIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { PieChart } from '@mui/x-charts/PieChart';
import { useQuery } from '@tanstack/react-query';
import EnquiryPipelineFunnel from '../components/ceo/EnquiryPipelineFunnel';
import { fetchCeoSummary, fetchCeoWpNewProperties } from '../services/ceoReportingService';
import {
  CEO_METRICS,
  CEO_PERIOD_STORAGE_KEY,
  buildCeoDetailUrl,
  parseCeoPeriod,
} from './ceo/ceoMetricRoutes';
import {
  BDM_CARD_SX,
  BDM_CHART_HEIGHT,
  BDM_KPI_HEIGHT,
  BDM_PANEL_HEIGHT,
  BDM_TABLE_HEIGHT,
  CeoDashboardPanel,
  CeoSectionTitle,
  GRID_SPACING,
  gridPanelItemSx,
} from './ceo/ceoDashboardLayout';

const audFmt = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  maximumFractionDigits: 0,
});
const numFmt = new Intl.NumberFormat('en-AU');

const dashboardCardSx = BDM_CARD_SX;
const TABLE_BODY_HEIGHT = BDM_TABLE_HEIGHT - 88;

const KPI_GRADIENTS = [
  { bg: 'radial-gradient(at top center,#aa57bf,#7d20a3)', label: 'Leads' },
  { bg: 'radial-gradient(at top center,#9c27b0,#6a1b9a)', label: 'Opportunities' },
  { bg: 'radial-gradient(at top center,#1fb7d7,#048ad2)', label: 'Properties booked' },
  { bg: 'radial-gradient(at top center,#5cb461,#45a248)', label: 'New on website' },
  { bg: 'radial-gradient(at top center,#5cb461,#2e7d32)', label: 'Closed Won' },
  { bg: 'radial-gradient(at top center,#fb991f,#e53935)', label: 'Closed Lost' },
  { bg: 'radial-gradient(at top center,#7d20a3,#5a1578)', label: 'Revenue' },
  { bg: 'radial-gradient(at top center,#fb991f,#f06f04)', label: 'Avg booking value' },
];

const CATEGORY_PIE_PALETTE = ['#aa57bf', '#1fb7d7', '#fb991f', '#5cb461', '#7d20a3', '#048ad2', '#e91e63', '#ff9800'];

const BOOKED_STAGES_LIST =
  'Booking Confirmed, Draft Contracts, Contract in DS, Signed Contract, Entry Instructions, or Closed Won';

function periodWindowHelp(period) {
  if (period === 'MONTH') {
    return 'Current window: last 30 days (Salesforce CreatedDate = LAST_N_DAYS:30). Comparison: prior 30 days (LAST_N_DAYS:60 through LAST_N_DAYS:30).';
  }
  return 'Current window: last 7 days (CreatedDate = LAST_N_DAYS:7). Comparison: prior 7 days (LAST_N_DAYS:14 through LAST_N_DAYS:7).';
}

/** Hover help text aligned with ceoSummaryService.js / wp-new-properties. */
function buildMetricTooltips(period) {
  const win = periodWindowHelp(period);
  return {
    leads: `Lead records created in the window (all leads, including converted). ${win} Shown separately from opportunities because lead fields differ.`,
    opportunities: `Opportunity records created in the window. ${win} Sector and pipeline metrics use opportunities only.`,
    totalEnquiries: `Deduped enquiry count for conversion % only: unconverted leads plus opportunities (converted leads count once as their opportunity). ${win}`,
    leadCategories: `Leads only — grouped by Lead Status (Status). ${win} Blank values shown as (blank). Not mixed with opportunity sector.`,
    opportunitySectors: `Opportunities only — sector from Type_of_H__c (Shoot) or Type_of_Event__c (Event record type). ${win} Fashion / TVC rollup where applicable.`,
    enquiries: `Combined deduped enquiry list (legacy drill-down). Prefer Leads or Opportunities KPIs.`,
    propertiesBooked: `Distinct opportunities that entered a “booked” stage during the window (OpportunityFieldHistory, StageName). ${win} Stages: ${BOOKED_STAGES_LIST}.`,
    newOnWebsite: `WordPress property posts published in the same rolling window as the period toggle (not Salesforce). Count uses publish date on the property CPT. Comparison uses the prior 7- or 30-day block.`,
    closedWon: `Distinct opportunities whose stage changed to Closed Won during the window (OpportunityFieldHistory, StageName). ${win}`,
    closedLost: `Distinct opportunities whose stage changed to Closed Lost during the window (OpportunityFieldHistory, StageName). ${win}`,
    revenue: `Sum of Total_Hire_Fees__c on opportunities that reached Closed Won in the window. ${win} Shown in AUD.`,
    avgBookingValue: `Closed Won revenue ÷ Closed Won opportunity count for the window. ${win} Zero if there are no Closed Won deals.`,
    winLossRatio: `Win rate = Closed Won ÷ (Closed Won + Closed Lost) × 100 for the current window only. ${win} Based on stage history transitions, not pipeline value.`,
    enquiryCategories: `Deprecated combined chart — use Lead categories and Opportunity sector instead.`,
    publishedPosts: `Lists WordPress property posts whose publish date falls in the current rolling window. ${win} KPI “New on website” uses the same filter (published count). Draft/scheduled rows appear only when WP API credentials allow.`,
    closedWonSector: `Closed Won in the window by sector — Event opps use Type_of_Event__c, Shoot opps use Type_of_H__c. ${win}`,
    closedLostReasons: `For each opportunity that reached Closed Lost in the window, grouped by Loss_Reason__c. ${win} Row counts are reasons; KPI Closed Lost is distinct opportunities.`,
    conversionBySector: `Per hire type: opportunities with a milestone stage in the window — Booking Confirmed, Contract in DS, or Closed Won (stage history). ${win} “% vs enquiries” = type milestone count ÷ total enquiries (same denominator for every type).`,
    avgBookingByBrand: `Average Total_Hire_Fees__c per Closed Won opportunity in the period, grouped by hire type. ${win}`,
    enquiryFunnel: `Live open opportunities by stage (same as BDM Enquiry Pipeline Status). Not period-scoped. Click a stage to see opportunity records.`,
  };
}

function clickHint(tooltip) {
  if (!tooltip) return undefined;
  return `${tooltip} Click to view underlying records.`;
}

function rowDrillHint(tooltip) {
  if (!tooltip) return undefined;
  return `${tooltip} Click a row to view matching records; use View all for the full list.`;
}

function WidgetTooltip({ title, children, clickable }) {
  if (!title) return children;
  return (
    <Tooltip
      title={
        <Typography component="div" variant="body2" sx={{ maxWidth: 400, lineHeight: 1.5 }}>
          {title}
        </Typography>
      }
      arrow
      placement="top"
      enterDelay={300}
      enterNextDelay={200}
    >
      <Box sx={{ cursor: clickable ? 'pointer' : 'help' }}>{children}</Box>
    </Tooltip>
  );
}

function formatDelta(current, previous, { isCurrency = false } = {}) {
  if (previous == null || current == null) return null;
  const c = Number(current);
  const p = Number(previous);
  const diff = c - p;
  if (Number.isNaN(diff)) return null;
  const pct = p !== 0 && Number.isFinite(p) ? ((diff / p) * 100).toFixed(1) : null;
  const arrow = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat';
  const diffPart = isCurrency
    ? `${diff >= 0 ? '+' : '−'}${audFmt.format(Math.abs(diff))}`
    : `${diff >= 0 ? '+' : ''}${numFmt.format(diff)}`;
  return { text: `${diffPart}${pct != null ? ` (${pct}%)` : ''}`, arrow };
}

function GradientKpiCard({
  title,
  value,
  previousValue,
  loading,
  isCurrency,
  gradient,
  subtitle,
  tooltip,
  detailUrl,
  onNavigate,
}) {
  const display = loading ? (
    <Skeleton width={100} height={36} sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
  ) : isCurrency ? (
    audFmt.format(Number(value) || 0)
  ) : (
    numFmt.format(Number(value) || 0)
  );

  const d = !loading && previousValue != null ? formatDelta(value, previousValue, { isCurrency }) : null;
  const DeltaIcon =
    d?.arrow === 'up' ? TrendingUpIcon : d?.arrow === 'down' ? TrendingDownIcon : TrendingFlatIcon;

  const cardInner = (
    <Card
      sx={{
        background: gradient,
        color: 'white',
        height: BDM_KPI_HEIGHT,
        display: 'flex',
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          flex: 1,
          padding: '12px 16px !important',
          width: '100%',
        }}
      >
        <Typography color="inherit" variant="subtitle2" fontWeight={600} gutterBottom sx={{ opacity: 0.95 }}>
          {title}
        </Typography>
        <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
          {display}
        </Typography>
        {d && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75, opacity: 0.9 }}>
            <DeltaIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" color="inherit">
              {d.text}
            </Typography>
          </Box>
        )}
        {subtitle && (
          <Typography variant="caption" color="inherit" sx={{ mt: 0.5, opacity: 0.85, fontSize: 10 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const card = detailUrl ? (
    <CardActionArea onClick={() => onNavigate(detailUrl)} sx={{ height: BDM_KPI_HEIGHT, maxHeight: BDM_KPI_HEIGHT }}>
      {cardInner}
    </CardActionArea>
  ) : (
    cardInner
  );

  return (
    <WidgetTooltip title={clickHint(tooltip)} clickable={Boolean(detailUrl)}>
      {card}
    </WidgetTooltip>
  );
}

function PanelWithTooltip({ tooltip, clickable, children }) {
  return (
    <WidgetTooltip title={clickHint(tooltip)} clickable={clickable}>
      {children}
    </WidgetTooltip>
  );
}

function CategoryPieSection({
  title,
  subtitle,
  rows,
  loading,
  tooltip,
  detailMetric,
  period,
  onNavigate,
  detailUrl,
}) {
  const pieData = useMemo(() => {
    return (rows || []).map((r, i) => ({
      id: i,
      value: r.count,
      label: r.name,
      color: CATEGORY_PIE_PALETTE[i % CATEGORY_PIE_PALETTE.length],
    }));
  }, [rows]);

  const total = pieData.reduce((s, d) => s + (d.value || 0), 0);

  const panel = (
    <CeoDashboardPanel
      title={title}
      subtitle={subtitle}
      icon={PeopleIcon}
      height={BDM_PANEL_HEIGHT}
      headerAction={
        detailUrl ? (
          <Link component="button" type="button" variant="caption" onClick={() => onNavigate(detailUrl)}>
            View all
          </Link>
        ) : null
      }
    >
      {loading ? (
        <Skeleton variant="rectangular" height={BDM_CHART_HEIGHT} />
      ) : pieData.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" sx={{ py: 8 }}>
          No data in this period.
        </Typography>
      ) : (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            {total} records · click slice for detail
          </Typography>
          <PieChart
            series={[
              {
                data: pieData,
                highlightScope: { fade: 'global', highlight: 'item' },
                innerRadius: 40,
                outerRadius: 120,
                paddingAngle: 2,
                cornerRadius: 4,
                arcLabel: (item) => (item.value > 0 ? String(item.value) : ''),
              },
            ]}
            height={BDM_CHART_HEIGHT}
            margin={{ top: 10, bottom: 10, left: 10, right: 120 }}
            onItemClick={(_, item) => {
              const label = item?.label ?? pieData[item?.dataIndex]?.label;
              if (label) {
                onNavigate(
                  buildCeoDetailUrl({
                    metric: detailMetric,
                    period,
                    window: 'current',
                    group: label,
                  })
                );
              }
            }}
            slotProps={{
              legend: {
                direction: 'column',
                position: { vertical: 'middle', horizontal: 'right' },
                itemMarkWidth: 10,
                itemMarkHeight: 10,
                labelStyle: { fontSize: 11 },
              },
            }}
          />
        </Box>
      )}
    </CeoDashboardPanel>
  );

  return <PanelWithTooltip tooltip={tooltip}>{panel}</PanelWithTooltip>;
}

/** Fixed-height table with pagination — keeps dashboard layout stable. */
function BoundedDataGrid({
  rows = [],
  columns,
  loading,
  height = TABLE_BODY_HEIGHT,
  pageSize = 8,
  onRowClick,
  sx,
  ...rest
}) {
  if (loading) {
    return <Skeleton variant="rectangular" height={height} sx={{ borderRadius: 1 }} />;
  }

  return (
    <DataGridPro
      rows={rows}
      columns={columns}
      autoHeight={false}
      disableColumnMenu
      disableRowSelectionOnClick
      density="compact"
      pageSizeOptions={[5, 8, 15, 25]}
      initialState={{ pagination: { paginationModel: { pageSize } } }}
      onRowClick={onRowClick}
      {...rest}
      sx={{
        border: 'none',
        height,
        width: '100%',
        ...sx,
      }}
    />
  );
}

const nameCountCols = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 140 },
  {
    field: 'count',
    headerName: 'Count',
    width: 100,
    type: 'number',
    align: 'right',
    headerAlign: 'right',
  },
];

const conversionCols = [
  { field: 'name', headerName: 'Sector', flex: 1, minWidth: 120 },
  {
    field: 'count',
    headerName: 'Milestone wins',
    width: 130,
    type: 'number',
    align: 'right',
    headerAlign: 'right',
  },
  {
    field: 'ratePercent',
    headerName: '% vs enquiries',
    width: 130,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params) => (params.value == null ? '—' : `${Number(params.value).toFixed(1)}%`),
  },
];

const avgBookingValueCols = [
  { field: 'name', headerName: 'Group', flex: 1, minWidth: 120 },
  { field: 'count', headerName: 'Deals', width: 80, type: 'number', align: 'right', headerAlign: 'right' },
  {
    field: 'averageBookingValue',
    headerName: 'Avg value',
    width: 120,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params) => audFmt.format(Number(params.value) || 0),
  },
  {
    field: 'totalRevenue',
    headerName: 'Total revenue',
    width: 130,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params) => audFmt.format(Number(params.value) || 0),
  },
];

const wpCols = [
  { field: 'title', headerName: 'Title', flex: 1, minWidth: 160 },
  { field: 'slug', headerName: 'Slug', width: 140 },
  { field: 'status', headerName: 'Status', width: 100 },
  { field: 'date', headerName: 'Published', width: 180 },
  {
    field: 'link',
    headerName: 'Link',
    width: 100,
    renderCell: (params) =>
      params.row.link ? (
        <Link href={params.row.link} target="_blank" rel="noopener noreferrer">
          View
        </Link>
      ) : null,
  },
];

export default function CeoReportingDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [diagOpen, setDiagOpen] = useState(false);

  const period = useMemo(() => {
    const fromUrl = searchParams.get('period');
    if (fromUrl) return parseCeoPeriod(fromUrl);
    try {
      return parseCeoPeriod(localStorage.getItem(CEO_PERIOD_STORAGE_KEY));
    } catch {
      return 'WEEK';
    }
  }, [searchParams]);

  useEffect(() => {
    try {
      localStorage.setItem(CEO_PERIOD_STORAGE_KEY, period);
    } catch {
      /* private browsing */
    }
    if (searchParams.get('period') !== period) {
      setSearchParams({ period }, { replace: true });
    }
  }, [period, searchParams, setSearchParams]);

  const handlePeriodChange = (_, value) => {
    if (!value) return;
    const next = parseCeoPeriod(value);
    try {
      localStorage.setItem(CEO_PERIOD_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    setSearchParams({ period: next }, { replace: true });
  };

  const goDetail = (url) => navigate(url);
  const detail = (metric, extra = {}) =>
    buildCeoDetailUrl({ metric, period, window: 'current', ...extra });

  const summaryQuery = useQuery({
    queryKey: ['ceoSummary', period],
    queryFn: () => fetchCeoSummary(period),
  });

  const wpQuery = useQuery({
    queryKey: ['ceoWp', period],
    queryFn: () => fetchCeoWpNewProperties(period),
  });

  const cur = summaryQuery.data?.current;
  const prev = summaryQuery.data?.previous;
  const loading = summaryQuery.isLoading;
  const err = summaryQuery.isError;
  const wpCur = wpQuery.data?.current;
  const wpPrev = wpQuery.data?.previous;

  const winLossPie = useMemo(() => {
    const won = Number(cur?.closedWonCount) || 0;
    const lost = Number(cur?.closedLostCount) || 0;
    if (won + lost === 0) return [];
    return [
      { id: 0, value: won, label: 'Closed Won', color: '#4CAF50' },
      { id: 1, value: lost, label: 'Closed Lost', color: '#f44336' },
    ];
  }, [cur]);

  const winRate =
    cur?.closedWonCount != null && cur?.closedLostCount != null
      ? Math.round(
          ((Number(cur.closedWonCount) || 0) /
            ((Number(cur.closedWonCount) || 0) + (Number(cur.closedLostCount) || 0) || 1)) *
            100
        )
      : 0;

  const periodSubtitle =
    summaryQuery.data?.currentPeriod?.label && summaryQuery.data?.previousPeriod?.label
      ? `${summaryQuery.data.currentPeriod.label} vs ${summaryQuery.data.previousPeriod.label} (equal-length windows)`
      : period === 'WEEK'
        ? 'Last 7 days vs prior 7 days'
        : 'Last 30 days vs prior 30 days';

  const tooltips = useMemo(() => buildMetricTooltips(period), [period]);

  const kpiItems = [
    {
      title: 'Leads',
      value: cur?.leadsCount,
      previous: prev?.leadsCount,
      isCurrency: false,
      gradient: KPI_GRADIENTS[0].bg,
      tooltip: tooltips.leads,
      detailMetric: CEO_METRICS.ENQUIRIES_LEADS,
    },
    {
      title: 'Opportunities',
      value: cur?.opportunitiesCount,
      previous: prev?.opportunitiesCount,
      isCurrency: false,
      gradient: KPI_GRADIENTS[1].bg,
      tooltip: tooltips.opportunities,
      subtitle:
        cur?.totalEnquiries != null ? `Deduped enquiries: ${numFmt.format(cur.totalEnquiries)}` : undefined,
      detailMetric: CEO_METRICS.ENQUIRIES_OPPORTUNITIES,
    },
    {
      title: 'Properties booked',
      value: cur?.propertiesBookedCount,
      previous: prev?.propertiesBookedCount,
      isCurrency: false,
      gradient: KPI_GRADIENTS[2].bg,
      tooltip: tooltips.propertiesBooked,
      detailMetric: CEO_METRICS.PROPERTIES_BOOKED,
    },
    {
      title: 'New on website',
      value: wpCur?.publishedCount ?? wpCur?.total,
      previous: wpPrev?.publishedCount ?? wpPrev?.total,
      loading: wpQuery.isLoading,
      isCurrency: false,
      gradient: KPI_GRADIENTS[3].bg,
      tooltip: tooltips.newOnWebsite,
      detailMetric: CEO_METRICS.NEW_ON_WEBSITE,
    },
    {
      title: 'Closed Won',
      value: cur?.closedWonCount,
      previous: prev?.closedWonCount,
      isCurrency: false,
      gradient: KPI_GRADIENTS[4].bg,
      tooltip: tooltips.closedWon,
      detailMetric: CEO_METRICS.CLOSED_WON,
    },
    {
      title: 'Closed Lost',
      value: cur?.closedLostCount,
      previous: prev?.closedLostCount,
      isCurrency: false,
      gradient: KPI_GRADIENTS[5].bg,
      tooltip: tooltips.closedLost,
      detailMetric: CEO_METRICS.CLOSED_LOST,
    },
    {
      title: 'Revenue',
      value: cur?.revenueInPeriod,
      previous: prev?.revenueInPeriod,
      isCurrency: true,
      gradient: KPI_GRADIENTS[6].bg,
      tooltip: tooltips.revenue,
      detailMetric: CEO_METRICS.REVENUE,
    },
    {
      title: 'Avg booking value',
      value: cur?.averageBookingValue,
      previous: prev?.averageBookingValue,
      isCurrency: true,
      gradient: KPI_GRADIENTS[7].bg,
      tooltip: tooltips.avgBookingValue,
      detailMetric: CEO_METRICS.AVG_BOOKING_VALUE,
    },
  ];

  const refreshAll = () => {
    summaryQuery.refetch();
    wpQuery.refetch();
  };

  return (
    <Box sx={{ pb: 3 }}>
      <Grid container spacing={GRID_SPACING} alignItems="stretch">
        {/* Header */}
        <Grid item xs={12}>
          <Card sx={dashboardCardSx}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AssessmentIcon
                    sx={{
                      fontSize: 36,
                      color: 'primary.main',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                    }}
                  />
                  <Box>
                    <Typography variant="h5" fontWeight={600}>
                      CEO reporting
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {periodSubtitle}
                    </Typography>
                    {summaryQuery.data?.generatedAt && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Last updated:{' '}
                        {new Date(summaryQuery.data.generatedAt).toLocaleString('en-AU')}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={period}
                    onChange={handlePeriodChange}
                    color="primary"
                  >
                    <ToggleButton value="WEEK">Last 7 days</ToggleButton>
                    <ToggleButton value="MONTH">Last 30 days</ToggleButton>
                  </ToggleButtonGroup>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={refreshAll}
                  >
                    Refresh
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {err && (
          <Grid item xs={12}>
            <Alert severity="error" action={<Button onClick={() => summaryQuery.refetch()}>Retry</Button>}>
              {summaryQuery.error?.message || 'Failed to load CEO summary.'}
            </Alert>
          </Grid>
        )}
        {(wpQuery.data?.authWarning || wpQuery.isError) && (
          <Grid item xs={12}>
            <Alert
              severity="warning"
              action={<Button onClick={() => wpQuery.refetch()}>Retry WP</Button>}
            >
              {wpQuery.data?.authWarning ||
                wpQuery.error?.message ||
                'WordPress property list failed.'}
            </Alert>
          </Grid>
        )}

        {/* ——— KPI strip (BDM: xs=6 md=3, 140px) ——— */}
        {kpiItems.map((kpi) => (
          <Grid item xs={6} md={3} key={kpi.title} sx={gridPanelItemSx}>
            <GradientKpiCard
              title={kpi.title}
              value={kpi.value}
              previousValue={kpi.previous}
              loading={kpi.loading || loading}
              isCurrency={kpi.isCurrency}
              gradient={kpi.gradient}
              subtitle={kpi.subtitle}
              tooltip={kpi.tooltip}
              detailUrl={kpi.detailMetric ? detail(kpi.detailMetric) : undefined}
              onNavigate={goDetail}
            />
          </Grid>
        ))}

        {/* ——— Row 2: Win/Loss | Lead mix | Opportunity sector (BDM 3× md=4 @ 420px) ——— */}
        <Grid item xs={12}>
          <CeoSectionTitle>Enquiry mix</CeoSectionTitle>
        </Grid>

        <Grid item xs={12} md={4} sx={gridPanelItemSx}>
          <PanelWithTooltip tooltip={tooltips.winLossRatio} clickable>
            <CeoDashboardPanel
              title="Win / Loss ratio"
              subtitle="Closed opportunities in period"
              icon={CheckCircleIcon}
              height={BDM_PANEL_HEIGHT}
              onClick={() => goDetail(detail(CEO_METRICS.CLOSED_WON))}
            >
              {loading ? (
                <Skeleton variant="circular" width={200} height={200} sx={{ mx: 'auto' }} />
              ) : winLossPie.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" sx={{ py: 8 }}>
                  No closed opportunities in this period.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 3,
                    height: BDM_CHART_HEIGHT,
                  }}
                >
                  <Box
                    sx={{
                      width: 200,
                      height: 200,
                      borderRadius: '50%',
                      flexShrink: 0,
                      background: `conic-gradient(#4CAF50 0% ${winRate}%, #f44336 ${winRate}% 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 150,
                        height: 150,
                        borderRadius: '50%',
                        bgcolor: 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography
                        variant="h4"
                        fontWeight={700}
                        color={winRate >= 50 ? '#4CAF50' : '#f44336'}
                      >
                        {winRate}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Win rate
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="body2" color="success.main" fontWeight={600}>
                      Won: {cur?.closedWonCount ?? 0}
                    </Typography>
                    <Typography variant="body2" color="error.main" fontWeight={600}>
                      Lost: {cur?.closedLostCount ?? 0}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CeoDashboardPanel>
          </PanelWithTooltip>
        </Grid>

        <Grid item xs={12} md={4} sx={gridPanelItemSx}>
          <CategoryPieSection
            title="Lead categories"
            subtitle="Lead Status · leads in period"
            rows={cur?.leadsByCategory}
            loading={loading}
            tooltip={tooltips.leadCategories}
            detailMetric={CEO_METRICS.LEAD_CATEGORIES}
            period={period}
            onNavigate={goDetail}
            detailUrl={detail(CEO_METRICS.LEAD_CATEGORIES)}
          />
        </Grid>

        <Grid item xs={12} md={4} sx={gridPanelItemSx}>
          <CategoryPieSection
            title="Opportunity sector"
            subtitle="Sector · opps in period (Event + Shoot)"
            rows={cur?.opportunitiesBySector}
            loading={loading}
            tooltip={tooltips.opportunitySectors}
            detailMetric={CEO_METRICS.OPPORTUNITY_SECTORS}
            period={period}
            onNavigate={goDetail}
            detailUrl={detail(CEO_METRICS.OPPORTUNITY_SECTORS)}
          />
        </Grid>

        {/* ——— Pipeline: funnel + published posts (6+6) ——— */}
        <Grid item xs={12}>
          <CeoSectionTitle>Pipeline</CeoSectionTitle>
        </Grid>

        <Grid item xs={12} md={6} sx={gridPanelItemSx}>
          <PanelWithTooltip tooltip={tooltips.enquiryFunnel} clickable>
            <EnquiryPipelineFunnel
              onStageClick={(stage) => goDetail(detail(CEO_METRICS.ENQUIRY_FUNNEL, { stage }))}
            />
          </PanelWithTooltip>
        </Grid>

        <Grid item xs={12} md={6} sx={gridPanelItemSx}>
          <PanelWithTooltip tooltip={tooltips.publishedPosts} clickable>
            <CeoDashboardPanel
              title="Published property posts"
              subtitle="WordPress property CPT"
              icon={LanguageIcon}
              height={BDM_TABLE_HEIGHT}
              onClick={() => goDetail(detail(CEO_METRICS.NEW_ON_WEBSITE))}
            >
              <BoundedDataGrid
                rows={(wpCur?.posts || []).map((r, i) => ({ id: r.id ?? i, ...r }))}
                columns={wpCols}
                loading={wpQuery.isLoading}
                pageSize={8}
                slots={{
                  noRowsOverlay: () => (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography color="text.secondary">No property posts in this period.</Typography>
                    </Box>
                  ),
                }}
              />
            </CeoDashboardPanel>
          </PanelWithTooltip>
        </Grid>

        {/* ——— Row 5: Closed deals breakdown (2×2 grid) ——— */}
        <Grid item xs={12}>
          <CeoSectionTitle>Closed deals &amp; conversion</CeoSectionTitle>
        </Grid>

        <Grid item xs={12} md={6} sx={gridPanelItemSx}>
          <BreakdownCard
            title="Closed Won by sector"
            rows={cur?.closedWonBySector}
            loading={loading}
            icon={CheckCircleIcon}
            tooltip={tooltips.closedWonSector}
            detailMetric={CEO_METRICS.CLOSED_WON_SECTOR}
            period={period}
            onNavigate={goDetail}
          />
        </Grid>
        <Grid item xs={12} md={6} sx={gridPanelItemSx}>
          <BreakdownCard
            title="Closed Lost reasons"
            rows={cur?.closedLostByReason}
            loading={loading}
            icon={CancelIcon}
            tooltip={tooltips.closedLostReasons}
            detailMetric={CEO_METRICS.CLOSED_LOST_REASONS}
            period={period}
            onNavigate={goDetail}
          />
        </Grid>
        <Grid item xs={12} md={6} sx={gridPanelItemSx}>
          <BreakdownCard
            title="Avg booking value by type"
            subtitle="Closed Won — by hire type"
            rows={cur?.averageBookingValueByBrand}
            loading={loading}
            columns={avgBookingValueCols}
            icon={AttachMoneyIcon}
            tooltip={tooltips.avgBookingByBrand}
            detailMetric={CEO_METRICS.AVG_BOOKING_BY_BRAND}
            period={period}
            onNavigate={goDetail}
          />
        </Grid>
        <Grid item xs={12} md={6} sx={gridPanelItemSx}>
          <BreakdownCard
            title="Conversion by type"
            subtitle="Milestone wins vs total enquiries"
            rows={cur?.conversionRateBySector}
            loading={loading}
            columns={conversionCols}
            icon={AttachMoneyIcon}
            tooltip={tooltips.conversionBySector}
            detailMetric={CEO_METRICS.CONVERSION_BY_SECTOR}
            period={period}
            onNavigate={goDetail}
          />
        </Grid>

        {/* Diagnostics — collapsed */}
        <Grid item xs={12}>
          <Card sx={dashboardCardSx}>
            <CardContent sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Salesforce field diagnostics
                </Typography>
                <IconButton size="small" onClick={() => setDiagOpen(!diagOpen)} aria-expanded={diagOpen}>
                  <ExpandMoreIcon sx={{ transform: diagOpen ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
                </IconButton>
              </Box>
              <Collapse in={diagOpen}>
                <Box
                  component="pre"
                  sx={{
                    mt: 1,
                    p: 1.5,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    fontSize: 12,
                    overflow: 'auto',
                  }}
                >
                  {JSON.stringify(cur?.resolvedFieldApiNames || {}, null, 2)}
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

function BreakdownCard({
  title,
  subtitle,
  rows,
  loading,
  columns = nameCountCols,
  icon: Icon,
  tooltip,
  detailMetric,
  period,
  onNavigate,
}) {
  const data = (rows || []).map((r, i) => ({ id: `row-${i}-${r.name}`, ...r }));
  const detailUrl = detailMetric
    ? buildCeoDetailUrl({ metric: detailMetric, period, window: 'current' })
    : undefined;
  const panelSubtitle =
    subtitle || (detailMetric ? 'Click a row to filter records' : undefined);

  return (
    <PanelWithTooltip tooltip={rowDrillHint(tooltip)} clickable={false}>
      <CeoDashboardPanel
        title={title}
        subtitle={panelSubtitle}
        icon={Icon}
        height={BDM_PANEL_HEIGHT}
        headerAction={
          detailUrl ? (
            <Link
              component="button"
              type="button"
              variant="caption"
              onClick={() => onNavigate(detailUrl)}
            >
              View all
            </Link>
          ) : null
        }
      >
        <BoundedDataGrid
          rows={data}
          columns={columns}
          loading={loading}
          height={BDM_PANEL_HEIGHT - 88}
          pageSize={8}
          hideFooter={data.length <= 8}
          onRowClick={
            detailMetric
              ? (params, event) => {
                  event?.stopPropagation?.();
                  onNavigate(
                    buildCeoDetailUrl({
                      metric: detailMetric,
                      period,
                      window: 'current',
                      group: params.row.name,
                    })
                  );
                }
              : undefined
          }
          sx={detailMetric ? { '& .MuiDataGrid-row': { cursor: 'pointer' } } : undefined}
        />
      </CeoDashboardPanel>
    </PanelWithTooltip>
  );
}
